import _ from 'lodash';
import { checkNull } from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import { initialSecondContact } from '../../lib/patientDetailsTab';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import type { SecondDrawerProps } from './SecondDrawer.types';

/**
 * Holds the editable secondary-contact form of the study's patient details drawer. Seeds merged state
 * from the study's secondary contact with an empty errorObject and wires a field change handler plus
 * the validated submit that passes the secondary-contact payload to handleClick.
 *
 * @param props - Study data and the parent save callback handleClick.
 * @returns An object carrying the form state, the field change handler and the submit handler.
 */
export const useSecondDrawer = ({
  data,
  handleClick,
}: Pick<SecondDrawerProps, 'data' | 'handleClick'>) => {
  const [state, setState] = useSetState<any>({
    ...initialSecondContact(data),
    errorObject: {},
  });

  /**
   * Writes one secondary-contact field and drops its validation error. Merges the value under its field name and clears that field's entry in errorObject.
   *
   * @param name - Secondary contact field being edited.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const handleChange = (name: any, value: any) => {
    setState({
      [name]: value,
      errorObject: {
        ...state.errorObject,
        [name]: '',
      },
    });
  };

  /**
   * Submits the patient's secondary contact details. Runs checkNull over the optional name, phone, relationship and e-mail fields and aborts with errorObject set when it reports anything, otherwise sends the trimmed values as a patient.secondaryContact payload through handleClick under the secondary-contact key.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const checkNullData = [];
    const errorObject = {};
    checkNullData.push(
      { field: 'name', value: state.name, isRequired: false },
      { field: 'phone', value: state.phone, isRequired: false },
      { field: 'relationship', value: state.relationship, isRequired: false },
      { field: 'email', value: state.email, isRequired: false },
    );

    const checkNullError = checkNull(checkNullData);
    if (!_.isEmpty(checkNullError)) {
      _.assign(errorObject, { ...checkNullError });
      setState({ errorObject });
      return;
    }
    const payload = {
      patient: {
        secondaryContact: {
          name: state?.name?.trim(),
          phone: state?.phone,
          relationship: state?.relationship?.trim(),
          email: state?.email?.trim(),
        },
      },
    };
    handleClick({
      payload,
      status: true,
      key: PATIENT_DETAIL_KEY.SECONDARY_CONTACT,
    });
  };

  return { state, handleChange, onClickBottomButton };
};
